/* ==========================================================================
   <fe-page-field> — the page's one ground, and the colour it is at.

   The page has a single honeycomb behind everything (fe-page-background).
   Sections paint no ground of their own; they only say whether the field
   should be light or teal where they are, with data-tone. This element reads
   those, works out where the colour should change, and moves the field there.

   Triggered, not dragged. This is the whole point, and it is the approved
   prototype's reasoning rather than ours:

     "a scrubbed transition can be PARKED. Stop scrolling halfway and the page
      sits in a half-finished phase indefinitely, which is where every contrast
      and legibility problem lives."

   A fade tied to scroll position can be held at its midpoint for as long as a
   reader likes, and the midpoint is where a colour change is at its worst —
   copy that starts lighter than the ground and ends darker has to pass through
   being exactly as bright as it. Measured on this theme's own palette, a
   heading parked there reads at about 2:1.

   So a boundary is a trigger. When one passes PLAY_AT the field animates to
   that mode over TRANSITION_MS on its own clock, whatever the scroll does
   next; the crossing lasts a fraction of that and cannot be stopped on. The
   two thresholds are apart so a scroll jittering on a boundary cannot make it
   stutter between the two.

   The mode is simply whichever boundary was passed last, which is what lets
   any number of dark stretches chain together with no bookkeeping per
   boundary: a dark section contributes two, one at each edge.

   What follows the field. The honeycomb writes every text role onto its host
   as --fe-*-color, contrast-guarded against whatever the ground is at that
   instant, so copy travels with the colour without this element knowing the
   first thing about type. Anything with two treatments rather than a range —
   a pill, a mark — reads data-field off the scope instead.
   ========================================================================== */

(() => {
  'use strict';

  if (customElements.get('fe-page-field')) return;

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

  /* The prototype's numbers, as its defaults. The section can change them,
     because they are a judgement about this page's rhythm rather than a
     constant: the trigger is where the join has to reach before the colour
     follows it, so a high one changes the ground while a good half-screen of
     the section above is still showing, and a low one waits until the new one
     nearly fills the screen. Short sections want a low one. */
  const DEFAULT_MS = 520;
  const DEFAULT_PLAY = 0.46;
  /* The gap between advancing and reverting. Without it a scroll resting on a
     join sits exactly on the threshold and flickers between the two. */
  const HYSTERESIS = 0.1;

  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  /* The progress runs at a flat rate and is not eased here, which looks like
     the wrong answer and is not.

     The ground eases what it is handed — fe-honeycomb.js puts every value
     through a smoothstep before it mixes a colour. Easing the clock as well
     puts the two curves in series, and in series they multiply: a cubic
     in-out feeding a smoothstep is three times as steep through the middle as
     the smoothstep alone. That is what makes the change read as a lurch — it
     is nearly still at both ends and then crosses almost all of the colour in
     a fraction of the time.

     So the clock is flat and the shaping is left to the one curve that was
     tuned for it. */

  class FePageField extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.honeycomb = this.querySelector('fe-honeycomb');
      if (!this.honeycomb) return;

      /* Everything the field is responsible for. The scope is also the
         honeycomb's host, so the text tokens land on the element every
         section is inside. */
      this.scope =
        this.closest('[data-fe-field-scope]') || document.getElementById('MainContent') || document.body;

      const play = parseFloat(this.dataset.play);
      const ms = parseFloat(this.dataset.ms);
      this.playAt = Number.isFinite(play) ? Math.min(0.9, Math.max(0.02, play)) : DEFAULT_PLAY;
      this.revertAt = this.playAt + HYSTERESIS;
      this.ms = Number.isFinite(ms) ? Math.max(0, ms) : DEFAULT_MS;

      this.mix = 0;
      this.target = null;
      this.from = 0;
      this.startedAt = -1;
      this.raf = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.step = this.step.bind(this);

      /* A section whose dark stretch cannot be read off its box asks for the
         field directly — the stacked hero, which is one screen that only turns
         teal at its last panel. A claim wins while it stands; releasing it
         hands the field back to the boundaries, and that hand-off is the
         transition into whatever is below. */
      this.claim = null;
      this.claimUntil = null;
      this.claimSpent = false;
      this.scope.addEventListener('fe-field:tone', (event) => {
        const detail = event.detail || {};
        this.claim = detail.tone === 'dark' ? 1 : detail.tone === 'light' ? 0 : null;
        /* Where the claim runs out, in document coordinates. A claim without
           one stands until it is released; a claim with one is released by the
           same line every other boundary uses, so a section that asks for the
           field also says where it stops asking. */
        this.claimUntil = Number.isFinite(detail.until) ? detail.until : null;
        this.claimSpent = false;
        this.settle(false);
      });

      addEventListener('scroll', this.onScroll, { passive: true });
      addEventListener('resize', this.onResize, { passive: true });

      /* Sections arrive and leave in the theme editor, and their heights
         settle after the webfont lands. Both change where a boundary is. */
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.measure());
        this.ro.observe(this.scope);
      }
      document.addEventListener('shopify:section:load', this.onResize);
      document.addEventListener('shopify:section:unload', this.onResize);
      document.fonts?.ready.then(this.onResize);

      this.measure();
      this.settle(true);
    }

    disconnectedCallback() {
      removeEventListener('scroll', this.onScroll);
      removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:load', this.onResize);
      document.removeEventListener('shopify:section:unload', this.onResize);
      this.ro?.disconnect();
      cancelAnimationFrame(this.raf);
    }

    /* Where the colour changes, in document coordinates. A stretch that wants
       a teal field contributes a boundary at each of its edges — into teal at
       the top, back to light at the bottom — so any number of them chain with
       nothing to keep in step. */
    measure() {
      const y = scrollY;
      this.marks = [];
      for (const el of this.scope.querySelectorAll('[data-tone="dark"]')) {
        const box = el.getBoundingClientRect();
        if (!box.height) continue;
        this.marks.push({ at: box.top + y, to: 1 }, { at: box.bottom + y, to: 0 });
      }
      this.marks.sort((a, b) => a.at - b.at);

      /* A dark stretch that runs to the end of the page keeps the field for
         good. Its lower edge is not a join between two colours — there is
         nothing below it but the footer, which is painted the same teal — so
         reverting there lays a band of light between two dark ones for the
         last screen of scroll, which is the flash of white on the way down and
         the flash of teal on the way back up.

         Judged by where the stretch ends rather than by which section it is,
         so it holds however the sections are ordered, and stops holding the
         moment anything light is added after it. */
      const last = this.marks[this.marks.length - 1];
      const scopeEnd = this.scope.getBoundingClientRect().bottom + y;
      if (last && last.to === 0 && scopeEnd - last.at < 4) this.marks.pop();

      this.passed = this.marks.map(() => false);
      this.onScroll();
    }

    /* the mode is the last boundary passed, with the two thresholds apart so a
       scroll sitting on one cannot flip it back and forth */
    read() {
      const vh = innerHeight || 1;

      if (this.claim !== null) {
        if (this.claimUntil === null) return this.claim;
        /* the two thresholds again, so a scroll resting on the end of a claim
           cannot flip it back and forth */
        const at = (this.claimUntil - scrollY) / vh;
        if (at < this.playAt) this.claimSpent = true;
        else if (at > this.revertAt) this.claimSpent = false;
        if (!this.claimSpent) return this.claim;
      }

      let mode = 0;
      this.marks.forEach((mark, i) => {
        const at = (mark.at - scrollY) / vh;
        if (at < this.playAt) this.passed[i] = true;
        else if (at > this.revertAt) this.passed[i] = false;
        if (this.passed[i]) mode = mark.to;
      });
      return mode;
    }

    onScroll() {
      if (this.queued) return;
      this.queued = true;
      requestAnimationFrame(() => {
        this.queued = false;
        this.settle(false);
      });
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.measure(), 120);
    }

    settle(immediate) {
      if (!this.marks) return;
      const want = this.read();
      if (want === this.target) return;
      this.target = want;

      /* The first paint has nowhere to travel from, and a reader who asked for
         less motion is given the ends rather than the journey between them. */
      if (immediate || REDUCED.matches || !this.ms) {
        this.apply(want);
        return;
      }
      this.from = this.mix;
      this.startedAt = performance.now();
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(this.step);
    }

    step(now) {
      const t = clamp01((now - this.startedAt) / this.ms);
      this.apply(this.from + (this.target - this.from) * t);
      if (t < 1) this.raf = requestAnimationFrame(this.step);
    }

    apply(mix) {
      this.mix = mix;
      this.honeycomb.setProgress?.(mix);
      /* One treatment either side for the things that cannot travel — a pill,
         a mark. Everything with a range of its own is already following the
         honeycomb's own tokens. */
      const shade = mix > 0.5 ? 'dark' : 'light';
      if (this.scope.dataset.field !== shade) this.scope.dataset.field = shade;
    }
  }

  customElements.define('fe-page-field', FePageField);
})();
